import { api } from "../http";
import type { ReferenceRecord } from "../../../components/reference/types";
import { buildReferenceCacheKey, isNavigatorOffline, searchCachedReferences } from "../../offline/referenceCache";

export type ConveyanceReferenceType =
  | "airlines"
  | "airline-representatives"
  | "flight-attendants"
  | "chemicals"
  | "disinfection-products"
  | "fumigation-providers"
  | "transport-companies"
  | "transport-drivers"
  | "transport-vehicles"
  | "transport-trailers"
  | "transport-vehicle-types"
  | "vessel-types"
  | "fumigation-methods";

function buildQuery(params: Record<string, unknown>) {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join("&");
}

const prefixFor = (type: ConveyanceReferenceType) => buildReferenceCacheKey("conveyance", type);

function path(type: ConveyanceReferenceType) {
  return `/api/conveyance/references/${type}`;
}

export const conveyanceReferencesApi = {
  async search(type: ConveyanceReferenceType, params: { q?: string; limit?: number } = {}) {
    if (isNavigatorOffline()) {
      const cached = await searchCachedReferences(prefixFor(type), params);
      if (cached) return cached;
    }
    try {
      const query = buildQuery(params);
      return await api.get<ReferenceRecord[]>(`${path(type)}${query ? `?${query}` : ""}`);
    } catch (error) {
      const cached = await searchCachedReferences(prefixFor(type), params);
      if (cached) return cached;
      throw error;
    }
  },
  async create(type: ConveyanceReferenceType, payload: Record<string, string | undefined>) {
    return api.post<ReferenceRecord, Record<string, string | undefined>>(path(type), payload);
  },
  async update(type: ConveyanceReferenceType, id: string, payload: Record<string, string | undefined>) {
    return api.put<ReferenceRecord, Record<string, string | undefined>>(
      `${path(type)}/${encodeURIComponent(id)}`,
      payload,
    );
  },
};

export default conveyanceReferencesApi;
